// Generator dokumen "Report Summary Improvement" (satu dokumen keseluruhan).
// Meta sama Clarification; Quantity/Pricing = blok teks per offer & per delivery (tanpa nested table).

import * as XLSX from 'xlsx';
import { displayValue } from '../../../utils/format';
import {
  formatPeriodRange,
  getContractTypeLabel,
  getOfferRowImprovementStatusLabel,
  getPaymentCurrencyLabel,
  formatProposalDeadline,
} from './tenderEvaluationHelpers';
import {
  formatOperationalTolerance,
  isValidOperationalToleranceValue,
  resolveOperationalToleranceOption,
  withBidderCounter,
} from '../../../shared/offer-domain/counter';
import { getVisibleProcurementClauses } from '../../../shared/offer-domain/respond';
import {
  getAlphaImprovementFieldValue,
  isOfferRowImprovement,
} from '../../../shared/offer-domain/improvement';
import {
  getProcurementProductNameId,
  isLpgProcurement,
  resolveDyedUndyedColumns,
  usesDyedUndyedAlpha,
} from '../../../shared/offer-domain/constants';
import {
  coalesceAlphaPrice,
  formatAlphaPrice,
  formatParcelSize,
  formatPortDateRange,
} from './deliveryDetailUtils';
import {
  getCapableOriginNamesFromFobLocations,
  isFobOriginDisplay,
  resolveCapableLocationSelections,
} from '../../../shared/offer-domain/delivery';
import { CARGO_RATIO_LABELS } from '../../../shared/offer-domain/cargoRatio';
import { resolveRequirementPaymentLabels } from './clarificationDocument';
import {
  buildHpsCatalog,
  getHpsAlphaValues,
  parseAlphaNumber,
} from '../components/detail/EvaluationOperational/Components/OfferTable/shared/hpsMatching';

const SELLER_NAME = 'PT Pertamina Patra Niaga';

const UI_SUBTITLE_NOISE = [
  'Click a clause to expand and edit its wording',
  'Click to view clause content',
  'Confirm the compliance requirements for this procurement',
  'Choose the payment methods and terms that apply',
  'Review procurement clauses and requirements for this tender',
  'Review procurement clauses and provide clarification responses',
  'Bisa jadi Kalimat tambahan untuk membantu section mengenai apa ini',
  'Contract and product requirements for this procurement',
];

const isValidDateForValidity = value => {
  if (value == null || value === '') return false;
  return !Number.isNaN(new Date(value).getTime());
};

const esc = s =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const plainText = value => {
  if (value == null || typeof value === 'object') return '';
  return String(value)
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const normalizeNoiseKey = value =>
  plainText(value)
    .toLowerCase()
    .replace(/[.\u2026]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const isUiSubtitleNoise = value => {
  const text = normalizeNoiseKey(value);
  if (!text) return false;
  return UI_SUBTITLE_NOISE.some(item => {
    const key = normalizeNoiseKey(item);
    return text === key || text.includes(key);
  });
};

const richHtml = value => {
  if (value == null || typeof value === 'object') return '-';
  if (isUiSubtitleNoise(value)) return '-';
  const text = plainText(value);
  if (!text) return '-';
  const raw = String(value);
  if (/<[a-z][\s\S]*>/i.test(raw)) return raw;
  return esc(raw);
};

const joinLawArbitration = procurement => {
  const law = displayValue(procurement?.law?.name, '');
  const arbitration = displayValue(procurement?.arbitration?.name, '');
  if (law && arbitration && law !== '-' && arbitration !== '-') {
    return `${law} / ${arbitration}`;
  }
  return law && law !== '-'
    ? law
    : arbitration && arbitration !== '-'
      ? arbitration
      : '-';
};

const formatOfferValidity = value => {
  const formatted = formatProposalDeadline(value);
  if (!formatted || formatted === '-') return '-';
  return `${formatted} (Jakarta Local Time)`;
};

const formatOtOptionLabel = procurement => {
  const normalizeOptionLabel = value => {
    let text = String(value ?? '')
      .trim()
      .replace(/\s+'/g, "'")
      .replace(/'\s+s\b/gi, "'s");
    if (!text || text === '-') return '';
    if (/'s\s+option/i.test(text)) {
      return text.replace(/'s\s+option/i, "'s Option");
    }
    if (/option/i.test(text)) {
      return text.replace(/\s*option/i, "'s Option");
    }
    return text;
  };

  const resolved = resolveOperationalToleranceOption({
    label: procurement?.operationalToleranceOptionsCounterLabel,
    optionsCounter: procurement?.operationalToleranceOptionsCounter,
  });
  if (resolved) return normalizeOptionLabel(resolved);
  return normalizeOptionLabel(displayValue(procurement?.partyType, ''));
};

const buildOtSummaryLine = (totalQtyLabel, procurement) => {
  const tolerance = isValidOperationalToleranceValue(
    procurement?.operationalToleranceCounter
  )
    ? procurement.operationalToleranceCounter
    : procurement?.operationalTolerance;
  const tolLabel = isValidOperationalToleranceValue(tolerance)
    ? `±${String(formatOperationalTolerance(tolerance)).replace(/%$/, '')}%`
    : null;
  const option = formatOtOptionLabel(procurement);
  if (!tolLabel && !option) return totalQtyLabel || '';
  if (tolLabel && option) {
    return `${totalQtyLabel} ${tolLabel} as operational tolerance at ${option}`;
  }
  if (tolLabel) {
    return `${totalQtyLabel} ${tolLabel} as operational tolerance`;
  }
  return `${totalQtyLabel} at ${option}`;
};

const resolveAddAldLabel = (addAld, incoterm) => {
  const raw = String(addAld ?? '')
    .trim()
    .toUpperCase();
  if (raw === 'ALD' || raw === 'ADD') return raw;
  const isFob =
    String(incoterm || '')
      .trim()
      .toUpperCase() === 'FOB';
  return isFob ? 'ALD' : 'ADD';
};

const hasPositiveQty = qty => {
  if (qty == null || qty === '') return false;
  const numeric = Number(String(qty).replace(/[^\d.-]/g, ''));
  return Number.isFinite(numeric) && numeric > 0;
};

const hasPortLabel = port => {
  const text = String(port ?? '').trim();
  return Boolean(text && text !== '-');
};

const buildLegLine = (
  qty,
  incoterm,
  route,
  port,
  addAld,
  startDate,
  endDate,
  unit
) => {
  if (!hasPositiveQty(qty) || !hasPortLabel(port)) return null;
  const qtyLabel = formatParcelSize(qty, unit);
  const place = [incoterm, route, port].filter(Boolean).join(' ').trim();
  const dateRange = formatPortDateRange(startDate, endDate);
  const addLabel = resolveAddAldLabel(addAld, incoterm);
  const datePart =
    dateRange && dateRange !== '-' ? ` on ${addLabel} ${dateRange}` : '';
  return `${qtyLabel}${place ? ` ${place}` : ''}${datePart}`.trim();
};

const resolveFobOriginLocationNames = detail => {
  if (detail?.fobOriginLocationName) {
    return [detail.fobOriginLocationName];
  }
  const selections =
    Array.isArray(detail?.selectedCapableLocations) &&
    detail.selectedCapableLocations.length
      ? detail.selectedCapableLocations
      : resolveCapableLocationSelections(detail);
  const selectedNames = selections
    .map(loc => loc?.name ?? loc?.Name)
    .filter(Boolean);
  if (selectedNames.length) return selectedNames;
  return getCapableOriginNamesFromFobLocations(detail?.incotermFOBLocations);
};

const resolveDetailForKind = (detailRow = {}, isCounter = false) => {
  if (!isCounter) {
    const port1 =
      detailRow.fobOriginLocationName ||
      detailRow.port1 ||
      detailRow.loadingPort;
    return {
      deliveryBasis: detailRow.deliveryBasis ?? detailRow.parcelSize,
      parcelSize: detailRow.parcelSize ?? detailRow.deliveryBasis,
      parcelSize2: detailRow.parcelSize2,
      parcelSize3: detailRow.parcelSize3,
      incoterm: detailRow.incoterm,
      route: detailRow.route,
      port1,
      port2: detailRow.port2,
      port3: detailRow.port3,
      addAld: detailRow.addAld,
      addAld2: detailRow.addAld2,
      addAld3: detailRow.addAld3,
      port1StartDate: detailRow.port1StartDate,
      port1EndDate: detailRow.port1EndDate,
      port2StartDate: detailRow.port2StartDate,
      port2EndDate: detailRow.port2EndDate,
      port3StartDate: detailRow.port3StartDate,
      port3EndDate: detailRow.port3EndDate,
      fobOriginLocationName: detailRow.fobOriginLocationName,
      isFobOriginLocationRow: detailRow.isFobOriginLocationRow,
      selectedCapableLocations: detailRow.selectedCapableLocations,
      incotermFOBLocations: detailRow.incotermFOBLocations,
      listIsCapable: detailRow.listIsCapable,
    };
  }

  const effectiveIncoterm = detailRow.counterIncoterm ?? detailRow.incoterm;
  const effectiveRoute = detailRow.counterRoute ?? detailRow.route;
  const isFob =
    String(effectiveIncoterm || '')
      .trim()
      .toUpperCase() === 'FOB';
  const port1 = isFob
    ? detailRow.counterLoadingPort ||
      detailRow.fobOriginLocationName ||
      detailRow.port1 ||
      detailRow.loadingPort
    : detailRow.counterLoadingPort || detailRow.port1 || detailRow.loadingPort;

  return {
    deliveryBasis:
      detailRow.counterDeliveryBasis ??
      detailRow.counterParcelSize ??
      detailRow.deliveryBasis ??
      detailRow.parcelSize,
    parcelSize:
      detailRow.counterParcelSize ??
      detailRow.counterDeliveryBasis ??
      detailRow.parcelSize ??
      detailRow.deliveryBasis,
    parcelSize2: detailRow.counterParcelSize2 ?? detailRow.parcelSize2,
    parcelSize3: detailRow.counterParcelSize3 ?? detailRow.parcelSize3,
    incoterm: effectiveIncoterm,
    route: effectiveRoute,
    port1,
    port2: detailRow.counterPort2 || detailRow.port2,
    port3: detailRow.counterPort3 || detailRow.port3,
    addAld: detailRow.counterAddAld ?? detailRow.addAld,
    addAld2: detailRow.counterAddAld2 ?? detailRow.addAld2,
    addAld3: detailRow.counterAddAld3 ?? detailRow.addAld3,
    port1StartDate: detailRow.counterStartDate ?? detailRow.port1StartDate,
    port1EndDate: detailRow.counterEndDate ?? detailRow.port1EndDate,
    port2StartDate: detailRow.counterStartDate2 ?? detailRow.port2StartDate,
    port2EndDate: detailRow.counterEndDate2 ?? detailRow.port2EndDate,
    port3StartDate: detailRow.counterStartDate3 ?? detailRow.port3StartDate,
    port3EndDate: detailRow.counterEndDate3 ?? detailRow.port3EndDate,
    fobOriginLocationName: detailRow.fobOriginLocationName,
    isFobOriginLocationRow: detailRow.isFobOriginLocationRow,
    selectedCapableLocations: detailRow.selectedCapableLocations,
    incotermFOBLocations: detailRow.incotermFOBLocations,
    listIsCapable: detailRow.listIsCapable,
  };
};

const legsFromDetail = (detail, unit) => {
  if (detail?.fobOriginLocationName || detail?.isFobOriginLocationRow) {
    return [
      buildLegLine(
        detail.parcelSize ?? detail.deliveryBasis,
        detail.incoterm,
        detail.route,
        detail.fobOriginLocationName || detail.port1,
        detail.addAld,
        detail.port1StartDate,
        detail.port1EndDate,
        unit
      ),
    ].filter(Boolean);
  }

  const fobNames = resolveFobOriginLocationNames(detail);
  if (isFobOriginDisplay(detail?.route, fobNames) && fobNames.length) {
    const qty = detail.parcelSize ?? detail.deliveryBasis;
    return fobNames
      .map(locationName =>
        buildLegLine(
          qty,
          detail.incoterm,
          detail.route,
          locationName,
          detail.addAld,
          detail.port1StartDate,
          detail.port1EndDate,
          unit
        )
      )
      .filter(Boolean);
  }

  return [
    buildLegLine(
      detail.parcelSize ?? detail.deliveryBasis,
      detail.incoterm,
      detail.route,
      detail.port1,
      detail.addAld,
      detail.port1StartDate,
      detail.port1EndDate,
      unit
    ),
    buildLegLine(
      detail.parcelSize2,
      detail.incoterm,
      detail.route,
      detail.port2,
      detail.addAld2,
      detail.port2StartDate,
      detail.port2EndDate,
      unit
    ),
    buildLegLine(
      detail.parcelSize3,
      detail.incoterm,
      detail.route,
      detail.port3,
      detail.addAld3,
      detail.port3StartDate,
      detail.port3EndDate,
      unit
    ),
  ].filter(Boolean);
};

const sumParcelQty = details => {
  let total = 0;
  let hasNumeric = false;
  details.forEach(detail => {
    [
      detail.parcelSize ?? detail.deliveryBasis,
      detail.parcelSize2,
      detail.parcelSize3,
    ].forEach(v => {
      if (!hasPositiveQty(v)) return;
      const n = Number(String(v).replace(/[^\d.-]/g, ''));
      if (Number.isFinite(n) && n > 0) {
        total += n;
        hasNumeric = true;
      }
    });
  });
  return hasNumeric ? total : null;
};

const isFilledAlphaPrice = value => {
  const text = String(value ?? '').trim();
  if (!text || text === '-') return false;
  return !/^-\s*(USD\/(?:BBL|MT))?$/i.test(text);
};

const buildPricingImprovementLines = (
  detailRow,
  kind,
  usesDyedUndyed,
  alphaSuffix
) => {
  if (usesDyedUndyed) {
    const columns = resolveDyedUndyedColumns(true, detailRow);
    const dyed = formatAlphaPrice(
      getAlphaImprovementFieldValue(detailRow, kind, 'dyed', {
        fallbackToBase: false,
      }),
      alphaSuffix
    );
    const undyed = formatAlphaPrice(
      getAlphaImprovementFieldValue(detailRow, kind, 'undyed', {
        fallbackToBase: false,
      }),
      alphaSuffix
    );
    return [
      columns.dyed && isFilledAlphaPrice(dyed)
        ? `Alpha Dyed Improvement: ${dyed}`
        : null,
      columns.undyed && isFilledAlphaPrice(undyed)
        ? `Alpha Undyed Improvement: ${undyed}`
        : null,
    ].filter(Boolean);
  }
  const alpha = formatAlphaPrice(
    getAlphaImprovementFieldValue(detailRow, kind, 'alpha', {
      fallbackToBase: false,
    }),
    alphaSuffix
  );
  return isFilledAlphaPrice(alpha) ? [`Alpha Improvement: ${alpha}`] : [];
};

// deliveryDetails dari OfferTable = flat detail; UI row = { bidder, detailRow, kind }.
const BIDDER_COUNTER_OT_KEYS = [
  'operationalToleranceCounter',
  'operationalToleranceOptionsCounter',
  'operationalToleranceOptionsCounterLabel',
  'proposalValidityPeriodMaxCounter',
];

const pickBidderCounterFromDetail = detail =>
  BIDDER_COUNTER_OT_KEYS.reduce((acc, key) => {
    if (detail && key in detail) acc[key] = detail[key];
    return acc;
  }, {});

export const normalizeImprovementOfferRows = (details = []) =>
  (Array.isArray(details) ? details : []).flatMap(detail => {
    if (detail?.detailRow && detail?.bidder) {
      const bidder = {
        ...detail.bidder,
        ...pickBidderCounterFromDetail(detail.detailRow),
        ...pickBidderCounterFromDetail(detail.bidder),
      };
      const row = { ...detail, bidder };
      return isOfferRowImprovement(row) ? [row] : [];
    }
    const bidder = {
      id: detail?.procurementMitraId ?? detail?.parentId ?? detail?.id,
      idOffer: detail?.idOffer,
      companyIdentityMask: detail?.companyIdentityMask,
      companyName: detail?.companyName,
      submissionDate: detail?.submissionDate,
      improvements: detail?.bidderImprovements ?? detail?.improvements,
      isLateSubmission: detail?.isLateSubmission,
      ...pickBidderCounterFromDetail(detail),
    };
    return ['offer', 'counter']
      .map(kind => ({ bidder, detailRow: detail, kind }))
      .filter(isOfferRowImprovement);
  });

const offerGroupKey = row =>
  String(
    row?.bidder?.idOffer ??
      row?.bidder?.id ??
      row?.detailRow?.procurementMitraId ??
      row?.detailRow?.id ??
      'offer'
  );

const buildDeliveryBlockHtml = ({
  row,
  cargoName,
  procurement,
  unit,
  usesDyedUndyed,
  alphaSuffix,
}) => {
  const detail = resolveDetailForKind(row.detailRow, row.kind === 'counter');
  const totalQty = sumParcelQty([detail]);
  const totalLabel =
    totalQty != null
      ? formatParcelSize(totalQty, unit)
      : formatParcelSize(detail.deliveryBasis, unit);
  const mergedOt =
    withBidderCounter(procurement, row.bidder) || procurement || {};
  const otLine = buildOtSummaryLine(totalLabel, mergedOt);
  const qtyLine =
    totalLabel && totalLabel !== '-'
      ? otLine && otLine !== totalLabel
        ? otLine
        : totalLabel
      : otLine || '';

  const legLines = legsFromDetail(detail, unit);
  const legsHtml = legLines.length
    ? `<div>${legLines
        .map(
          (line, i) =>
            `${esc(line)}${i < legLines.length - 1 ? esc(', and ') : ''}`
        )
        .join('')}</div>`
    : '';

  const pricingLines = buildPricingImprovementLines(
    row.detailRow,
    row.kind,
    usesDyedUndyed,
    alphaSuffix
  );
  const pricingHtml = pricingLines.length
    ? `<div>${pricingLines.map(line => esc(line)).join(' · ')}</div>`
    : '';

  return `<div class="cargo">
    <div class="cargo-name">${esc(cargoName)}</div>
    ${qtyLine ? `<div>${esc(qtyLine)}</div>` : ''}
    ${legsHtml}
    ${pricingHtml}
  </div>`;
};

const buildImprovementQuantityHtml = (rows, procurement) => {
  const improvementRows = normalizeImprovementOfferRows(rows);
  if (!improvementRows.length) {
    return '<div class="muted">Belum ada data improvement.</div>';
  }

  const isLpg = isLpgProcurement(procurement);
  const usesDyedUndyed = usesDyedUndyedAlpha(
    getProcurementProductNameId(procurement)
  );
  const unit = isLpg ? 'MT' : procurement?.productType?.unit || 'MB';
  const alphaSuffix = isLpg ? 'USD/MT' : 'USD/BBL';

  const offerOrder = [];
  const offerMap = new Map();
  improvementRows.forEach(row => {
    const key = offerGroupKey(row);
    if (!offerMap.has(key)) {
      offerMap.set(key, []);
      offerOrder.push(key);
    }
    offerMap.get(key).push(row);
  });

  const offerBlocks = offerOrder.map(offerKey => {
    const offerRows = offerMap.get(offerKey) || [];
    const bidder = offerRows[0]?.bidder;
    const idOffer = displayValue(bidder?.idOffer, '-');
    const late = bidder?.isLateSubmission === true ? ' / Late' : '';
    const improvementStatus = getOfferRowImprovementStatusLabel(offerRows[0]);
    const offerTitle = `${idOffer}${late} · ${improvementStatus}`;

    const deliveryHtml = offerRows
      .map((row, deliveryIndex) => {
        const unitIndex = Number.isFinite(
          Number(row.detailRow?.deliveryUnitIndex)
        )
          ? Number(row.detailRow.deliveryUnitIndex)
          : deliveryIndex;
        const cargoName =
          row.detailRow?.deliveryUnit ||
          row.detailRow?.DeliveryUnit ||
          `Cargo ${String.fromCharCode(65 + unitIndex)}`;
        return buildDeliveryBlockHtml({
          row,
          cargoName,
          procurement,
          unit,
          usesDyedUndyed,
          alphaSuffix,
        });
      })
      .join('');

    return `<div class="offer">
      <div class="offer-name">${esc(offerTitle)}</div>
      ${deliveryHtml}
    </div>`;
  });

  return offerBlocks.join('');
};

const metaRow = (label, valueHtml) =>
  `<tr>
    <td class="label">${esc(label)}</td>
    <td class="value">${valueHtml}</td>
  </tr>`;

const buildPaymentModeHtml = (procurement, modeLabel) => {
  const mode = displayValue(
    procurement?.modeInvoicingQuantity?.name || modeLabel,
    '-'
  );
  return mode && mode !== '-' ? esc(mode) : '-';
};

export const buildImprovementHtml = ({
  procurement,
  rows = [],
  modeLabel = null,
} = {}) => {
  const merged = procurement || {};
  const productName = displayValue(merged?.productType?.product?.name, '-');
  const period = formatPeriodRange(merged);
  const contractType = getContractTypeLabel(merged?.contractType);
  const intro = `Based on our evaluation and improvement results for the ${productName} supply purchase ${period} Delivery (${contractType}), we are pleased to share the summary details as follows:`;

  const validityValue = isValidDateForValidity(
    merged?.proposalValidityPeriodMaxCounter
  )
    ? merged.proposalValidityPeriodMaxCounter
    : merged?.proposalValidityPeriodMax;

  const clauses = getVisibleProcurementClauses(merged);
  const clauseRows = clauses.length
    ? clauses
        .map(clause => {
          const title = plainText(clause.clauseTitle) || 'Clause Detail';
          return metaRow(title, richHtml(clause.clauseDescription));
        })
        .join('')
    : metaRow('Clause Detail', '-');

  const procNo = displayValue(merged?.procurementNumber, '');

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Report Tender Evaluation_Improvement Offer ${procNo ? ` ${esc(procNo)}` : ''}</title>
<style>
  body { font-family: Calibri, Arial, Helvetica, sans-serif; font-size: 12px; color: #222; margin: 0; background: #9a9a9a; }
  .sheet { width: 210mm; min-height: 297mm; margin: 24px auto; padding: 15mm 18mm; background: #fff; box-shadow: 0 2px 12px rgba(0,0,0,.45); box-sizing: border-box; }
  h1 { font-size: 16px; text-align: center; margin: 0 0 14px; font-weight: bold; text-transform: uppercase; }
  .intro { margin: 0 0 16px; line-height: 1.45; }
  table.meta { border-collapse: collapse; width: 100%; }
  table.meta td { border: 0.35mm solid #555; padding: 6px 8px; vertical-align: top; }
  table.meta td.label { width: 32%; font-weight: bold; background: #f7f7f7; }
  table.meta td.value { width: 68%; }
  .offer { margin: 0 0 8px; }
  .offer-name { font-weight: bold; margin: 0 0 3px; }
  .cargo { margin: 0 0 6px; line-height: 1.35; }
  .cargo-name { font-weight: bold; }
  .cargo div { margin: 0; }
  .muted { color: #666; }
  @media print {
    @page { size: A4; margin: 15mm; }
    body { margin: 0; background: #fff; }
    .sheet { width: auto; min-height: 0; margin: 0; padding: 0; background: #fff; box-shadow: none; }
  }
</style>
</head>
<body>
<div class="sheet">
  <h1>Report Summary Improvement</h1>
  <p class="intro">${esc(intro)}</p>
  <table class="meta">
    ${metaRow('Seller', esc(SELLER_NAME))}
    ${metaRow('Product', esc(productName))}
    ${metaRow('Contract Period', esc(period))}
    ${metaRow('Product Specification', richHtml(merged?.productSpecification))}
    ${metaRow(
      'Quantity & Mode of Delivery / Pricing Improvement',
      buildImprovementQuantityHtml(rows, merged)
    )}
    ${clauseRows}
    ${metaRow('Payment Mode', buildPaymentModeHtml(merged, modeLabel))}
    ${metaRow(
      'Payment Currency',
      esc(getPaymentCurrencyLabel(merged?.paymentCurrency))
    )}
    ${metaRow('Law & Arbitration', esc(joinLawArbitration(merged)))}
    ${metaRow(
      'Other Terms & Condition',
      richHtml(merged?.otherTermsAndCondition)
    )}
    ${metaRow('Offer Validity', esc(formatOfferValidity(validityValue)))}
    ${metaRow('Additional Notes', richHtml(merged?.additionalNotes))}
  </table>
</div>
</body>
</html>`;
};

const openDocWindow = html => {
  const w = window.open('', '_blank');
  if (!w) return null;
  w.document.write(html);
  w.document.close();
  return w;
};

export const printImprovementPdf = async ({ procurement, rows }) => {
  const { modeLabel } = await resolveRequirementPaymentLabels(procurement);
  const html = buildImprovementHtml({ procurement, rows, modeLabel });
  const w = openDocWindow(html);
  if (!w) return;
  w.focus();
  setTimeout(() => w.print(), 400);
};

const stripAlphaUnit = value => {
  if (value == null || value === '') return '';
  const text = String(value).trim();
  if (!text || text === '-') return '';
  return text.replace(/\s*USD\/(?:BBL|MT)\s*/gi, '').trim();
};

const resolveOfferAlphaDyed = (detailRow, kind) =>
  stripAlphaUnit(
    kind === 'counter'
      ? coalesceAlphaPrice(
          detailRow.counterDyed,
          detailRow.dyed2,
          detailRow.dyed,
          detailRow.unitBbl
        )
      : coalesceAlphaPrice(detailRow.dyed, detailRow.unitBbl)
  );

const resolveOfferAlphaUndyed = (detailRow, kind) =>
  stripAlphaUnit(
    kind === 'counter'
      ? coalesceAlphaPrice(
          detailRow.counterUndyed,
          detailRow.undyed2,
          detailRow.undyed,
          detailRow.cbm
        )
      : coalesceAlphaPrice(detailRow.undyed, detailRow.cbm)
  );

const resolveOfferAlpha = (detailRow, kind) =>
  stripAlphaUnit(
    kind === 'counter'
      ? coalesceAlphaPrice(
          detailRow.counterAlpha,
          detailRow.alpha2,
          detailRow.alphaMitra2,
          detailRow.alpha,
          detailRow.alphaMitra1
        )
      : coalesceAlphaPrice(
          detailRow.alpha,
          detailRow.alphaMitra1,
          detailRow.alpha1
        )
  );

const resolveImproveAlpha = (detailRow, kind, field = 'alpha') =>
  stripAlphaUnit(getAlphaImprovementFieldValue(detailRow, kind, field));

const formatSelisihHpsVsImprove = (improveValue, hpsValue) => {
  const improve = parseAlphaNumber(improveValue);
  const hps = parseAlphaNumber(hpsValue);
  if (improve == null || hps == null) return '';
  const diff = hps - improve;
  return Number.isInteger(diff)
    ? String(diff)
    : String(Number(diff.toFixed(6)));
};

const resolveCargoRatioLabel = (detailRow, counterRatio, kind = 'offer') => {
  if (kind === 'counter') {
    if (counterRatio?.label) return String(counterRatio.label).trim();
    const counterRatioId = Number(counterRatio?.id ?? counterRatio?.cargoRatio);
    if (Number.isFinite(counterRatioId) && counterRatioId >= 1) {
      return CARGO_RATIO_LABELS[counterRatioId - 1] ?? String(counterRatioId);
    }
    return '';
  }
  if (detailRow?.cargoRatioLabel)
    return String(detailRow.cargoRatioLabel).trim();
  if (detailRow?.ratioLpg && typeof detailRow.ratioLpg === 'string') {
    return detailRow.ratioLpg;
  }
  if (detailRow?.ratioLpg?.ratioName) return detailRow.ratioLpg.ratioName;
  if (detailRow?.ratioLpg?.name) return detailRow.ratioLpg.name;
  const ratioId = Number(detailRow?.cargoRatio);
  if (Number.isFinite(ratioId) && ratioId >= 1) {
    return CARGO_RATIO_LABELS[ratioId - 1] ?? String(ratioId);
  }
  return '';
};

const resolveOfferValidity = (procurement, bidder) => {
  const merged = withBidderCounter(procurement, bidder) || procurement || {};
  const validityValue = isValidDateForValidity(
    merged?.proposalValidityPeriodMaxCounter
  )
    ? merged.proposalValidityPeriodMaxCounter
    : merged?.proposalValidityPeriodMax;
  const formatted = formatProposalDeadline(validityValue);
  return formatted && formatted !== '-' ? formatted : '';
};

const resolveExcelParcelLabel = detail => {
  const parcelQty = detail?.parcelSize ?? detail?.deliveryBasis;
  if (!hasPositiveQty(parcelQty)) return '';
  const numeric = Number(String(parcelQty).replace(/[^\d.-]/g, ''));
  return String(Number.isFinite(numeric) ? numeric : parcelQty);
};

const expandFobOriginExcelRows = row => {
  const detail = row.detailRow || {};
  if (
    detail.fobOriginLocationName ||
    detail.isFobOriginLocationRow ||
    detail.incotermLocationId
  ) {
    return [row];
  }

  const resolved = resolveDetailForKind(detail, row.kind === 'counter');
  const fobNames = resolveFobOriginLocationNames(resolved);
  if (isFobOriginDisplay(resolved?.route, fobNames) && fobNames.length) {
    return fobNames.map(locationName => ({
      ...row,
      detailRow: {
        ...detail,
        fobOriginLocationName: locationName,
        isFobOriginLocationRow: true,
      },
    }));
  }

  return [row];
};

const expandLpgCounterExcelRows = counterRows => {
  if (!counterRows.length) return [];

  const groups = new Map();
  counterRows.forEach(row => {
    const groupKey = [
      row.bidder?.id ?? row.bidder?.idOffer ?? '',
      row.detailRow?.quantityIncotermCounterId ??
        row.detailRow?.quantityIncotermId ??
        '',
      row.detailRow?.quantityDeliveryTermId ?? '',
    ].join('|');
    if (!groups.has(groupKey)) {
      groups.set(groupKey, { rows: [] });
    }
    groups.get(groupKey).rows.push(row);
  });

  const expanded = [];
  groups.forEach(group => {
    const parcelByLabel = new Map();
    group.rows.forEach(row => {
      const label = resolveExcelParcelLabel(
        resolveDetailForKind(row.detailRow, true)
      );
      if (!label) return;
      const current = parcelByLabel.get(label);
      if (
        !current ||
        (!current.detailRow?.quantityInformationDetailCounterId &&
          row.detailRow?.quantityInformationDetailCounterId)
      ) {
        parcelByLabel.set(label, row);
      }
    });

    const counterRatios = group.rows.find(
      row =>
        Array.isArray(row.detailRow?.counterCargoRatios) &&
        row.detailRow.counterCargoRatios.length
    )?.detailRow?.counterCargoRatios ?? [null];

    counterRatios.forEach(counterRatio => {
      parcelByLabel.forEach(row => {
        expanded.push({ ...row, counterRatio, kind: 'counter' });
      });
    });
  });

  return expanded;
};

const expandImprovementExcelRows = (improvementRows, procurement) => {
  const isLpg = isLpgProcurement(procurement);
  if (!isLpg) {
    return improvementRows.flatMap(expandFobOriginExcelRows);
  }

  const offerRows = improvementRows
    .filter(row => row.kind !== 'counter')
    .flatMap(expandFobOriginExcelRows);
  const counterRows = expandLpgCounterExcelRows(
    improvementRows.filter(row => row.kind === 'counter')
  );
  return [...offerRows, ...counterRows];
};

const buildImprovementExcelDetailRow = (row, counterRatio) => {
  if (!counterRatio) return row.detailRow;
  const counterParcelSize =
    counterRatio.parcelSize ?? row.detailRow?.cargoRatioParcelSize ?? null;
  return {
    ...row.detailRow,
    counterCargoRatioRecordId:
      counterRatio.recordId ?? row.detailRow?.counterCargoRatioRecordId ?? null,
    counterCargoRatioId: counterRatio.id ?? row.detailRow?.counterCargoRatioId,
    quantityInformationCargoRatioId:
      counterRatio.quantityInformationCargoRatioFK ??
      row.detailRow?.quantityInformationCargoRatioId,
    cargoRatioParcelSize: counterParcelSize,
    counterParcelSize,
    counterDeliveryBasis:
      counterParcelSize ?? row.detailRow?.counterDeliveryBasis,
    cargoRatioAlphaImprovement:
      counterRatio.alphaImprovement ??
      counterRatio.cargoRatioAlphaImprovement ??
      row.detailRow?.cargoRatioAlphaImprovement ??
      null,
    improvementAlpha:
      counterRatio.alphaImprovement ?? row.detailRow?.improvementAlpha ?? null,
    improvementAlpha2:
      counterRatio.alphaImprovement ?? row.detailRow?.improvementAlpha2 ?? null,
    alphaHps: counterRatio.alphaHps ?? row.detailRow?.alphaHps,
    alphaDyedHps: counterRatio.alphaDyedHps ?? row.detailRow?.alphaDyedHps,
    alphaUndyedHps:
      counterRatio.alphaUndyedHps ?? row.detailRow?.alphaUndyedHps,
    counterAlpha: coalesceAlphaPrice(
      counterRatio.alpha,
      row.detailRow?.counterAlpha
    ),
    counterDyed: coalesceAlphaPrice(
      counterRatio.dyed,
      counterRatio.alphaDyed,
      row.detailRow?.counterDyed,
      row.detailRow?.dyed2
    ),
    counterUndyed: coalesceAlphaPrice(
      counterRatio.undyed,
      counterRatio.alphaUndyed,
      row.detailRow?.counterUndyed,
      row.detailRow?.undyed2
    ),
  };
};

export const downloadImprovementExcel = ({ procurement, rows = [] } = {}) => {
  const improvementRows = normalizeImprovementOfferRows(rows);
  const expanded = expandImprovementExcelRows(improvementRows, procurement);
  const isLpg = isLpgProcurement(procurement);
  const usesDyedUndyed = usesDyedUndyedAlpha(
    getProcurementProductNameId(procurement)
  );
  const unit = isLpg ? 'MT' : 'MB';
  const alphaUnit = isLpg ? 'USD/MT' : 'USD/BBL';
  const hpsCatalog = buildHpsCatalog({ deliveryDetails: rows });

  // One sheet holds every parcel, so a Dyed / Undyed column is kept when any
  // parcel asks for it; rows of parcels that did not ask for it show N/A.
  const rowColumns = row =>
    resolveDyedUndyedColumns(usesDyedUndyed, row.detailRow);
  const sheetColumns = {
    dyed: expanded.some(row => rowColumns(row).dyed),
    undyed: expanded.some(row => rowColumns(row).undyed),
  };
  const alphaHeaders = label =>
    usesDyedUndyed
      ? [
          ...(sheetColumns.dyed ? [`${label} DYED (${alphaUnit})`] : []),
          ...(sheetColumns.undyed ? [`${label} UNDYED (${alphaUnit})`] : []),
        ]
      : [`${label} (${alphaUnit})`];
  const alphaCells = (columns, dyed, undyed, single) =>
    usesDyedUndyed
      ? [
          ...(sheetColumns.dyed ? [columns.dyed ? dyed : 'N/A'] : []),
          ...(sheetColumns.undyed ? [columns.undyed ? undyed : 'N/A'] : []),
        ]
      : [single];

  const alphaOfferHeaders = alphaHeaders('ALPHA OFFER');
  const alphaImproveHeaders = alphaHeaders('ALPHA IMPROVE');
  const alphaFinalHeaders = alphaHeaders('ALPHA FINAL');
  const hpsHeaders = alphaHeaders('HPS');
  const selisihHeaders = alphaHeaders('SELISIH HPS VS IMPROVE');

  const headers = [
    'No',
    'COUNTERPARTY',
    'STATUS',
    'DATE & TIME SUBMIT OFFER',
    'CARGO',
    ...(isLpg ? ['CARGO RATIO'] : []),
    `PARCEL SIZE (${unit})`,
    'MODE OF DELIVERY',
    'LAYCAN',
    'OFFER VALIDITY',
    ...alphaOfferHeaders,
    ...alphaImproveHeaders,
    ...alphaFinalHeaders,
    `DELAY PENALTI (${alphaUnit})`,
    ...hpsHeaders,
    ...selisihHeaders,
    'NOTES',
  ];

  const data = expanded.map((row, index) => {
    const detailRow = buildImprovementExcelDetailRow(row, row.counterRatio);
    const columns = rowColumns(row);
    const isCounter = row.kind === 'counter';
    const detail = resolveDetailForKind(detailRow, isCounter);
    const cargoName =
      detailRow?.deliveryUnit ||
      detailRow?.DeliveryUnit ||
      (Number.isFinite(Number(detailRow?.deliveryUnitIndex))
        ? `Cargo ${String.fromCharCode(65 + Number(detailRow.deliveryUnitIndex))}`
        : '-');
    const parcelLabel = resolveExcelParcelLabel(detail);
    const modeOfDelivery = [detail.incoterm, detail.route]
      .filter(Boolean)
      .join(' ')
      .trim();
    const laycan = formatPortDateRange(
      detail.port1StartDate,
      detail.port1EndDate
    );
    const offerDyed = usesDyedUndyed
      ? resolveOfferAlphaDyed(detailRow, row.kind)
      : null;
    const offerUndyed = usesDyedUndyed
      ? resolveOfferAlphaUndyed(detailRow, row.kind)
      : null;
    const alphaOffer = usesDyedUndyed
      ? null
      : resolveOfferAlpha(detailRow, row.kind);
    const improveDyed = usesDyedUndyed
      ? resolveImproveAlpha(detailRow, row.kind, 'dyed')
      : null;
    const improveUndyed = usesDyedUndyed
      ? resolveImproveAlpha(detailRow, row.kind, 'undyed')
      : null;
    const alphaImprove = usesDyedUndyed
      ? null
      : resolveImproveAlpha(detailRow, row.kind, 'alpha');
    const hps = getHpsAlphaValues(detailRow, {
      isCounter,
      hpsCatalog,
    });
    const submitLabel = formatProposalDeadline(row.bidder?.submissionDate);

    return [
      index + 1,
      displayValue(row.bidder?.companyName || row.bidder?.idOffer, ''),
      isCounter ? 'Counter' : 'Compliant',
      submitLabel && submitLabel !== '-' ? submitLabel : '',
      cargoName,
      ...(isLpg
        ? [resolveCargoRatioLabel(detailRow, row.counterRatio, row.kind)]
        : []),
      parcelLabel,
      modeOfDelivery || '',
      laycan && laycan !== '-' ? laycan : '',
      resolveOfferValidity(procurement, row.bidder),
      ...alphaCells(
        columns,
        offerDyed || '',
        offerUndyed || '',
        alphaOffer || ''
      ),
      ...alphaCells(
        columns,
        improveDyed || '',
        improveUndyed || '',
        alphaImprove || ''
      ),
      ...alphaCells(columns, '', '', ''),
      '',
      ...alphaCells(
        columns,
        stripAlphaUnit(hps?.dyed),
        stripAlphaUnit(hps?.undyed),
        stripAlphaUnit(hps?.alpha)
      ),
      ...alphaCells(
        columns,
        formatSelisihHpsVsImprove(improveDyed, hps?.dyed),
        formatSelisihHpsVsImprove(improveUndyed, hps?.undyed),
        formatSelisihHpsVsImprove(alphaImprove, hps?.alpha)
      ),
      '',
    ];
  });

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);
  worksheet['!cols'] = headers.map((header, colIndex) => {
    const maxLen = Math.max(
      String(header ?? '').length,
      ...data.map(row => String(row[colIndex] ?? '').length)
    );
    return { wch: Math.min(Math.max(maxLen + 2, 8), 48) };
  });

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Improvement Offer');

  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  const dateStr = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  const procNo = displayValue(procurement?.procurementNumber, 'data');
  XLSX.writeFile(
    workbook,
    `Report Tender Evaluation_Improvement Offer ${procNo}_${dateStr}.xlsx`
  );
  return true;
};
